// Renders a commit message body as markdown through the EXACT sanitized pipeline
// the editor uses (@slate/core renderToHast = the rehype-sanitize boundary),
// then hydrates the sanitized hast to React with rehype-react, the same way
// PluginReadme / RenderedPreview do. We add NO sanitizer of our own and never
// weaken the boundary: sanitize has already stripped <script>, event handlers,
// javascript: hrefs, and every data: URL, so a hostile commit message is inert.
// Links are rendered but click-inert (a commit message must not navigate the app
// away); images resolve to nothing since a repo message cannot bundle assets.

import { useMemo, type ComponentType, type JSX, type ReactElement } from 'react';
import { renderToHast } from '@slate/core';
import rehypeReact from 'rehype-react';
import { unified, type Processor } from 'unified';
import { Fragment, jsx, jsxs } from 'react/jsx-runtime';
import type { Root as HastRoot } from 'hast';

export interface CommitBodyMarkdownProps {
  /** The commit message body (everything after the subject line). */
  body: string;
}

type RehypeReactProcessor = Processor<undefined, undefined, undefined, HastRoot, ReactElement>;

const InertAnchor: ComponentType<JSX.IntrinsicElements['a']> = ({ children, ...rest }) => (
  <a {...rest} onClick={(e) => e.preventDefault()}>
    {children}
  </a>
);

const BlankImg: ComponentType<JSX.IntrinsicElements['img']> = ({ alt, ...rest }) => (
  <img {...rest} alt={typeof alt === 'string' ? alt : ''} src="" />
);

export function CommitBodyMarkdown({ body }: CommitBodyMarkdownProps): ReactElement {
  const element = useMemo(() => {
    const tree = renderToHast(body); // sanitized hast (the security boundary)
    const processor = unified().use(rehypeReact, {
      Fragment,
      jsx,
      jsxs,
      components: { a: InertAnchor, img: BlankImg },
    }) as RehypeReactProcessor;
    return processor.stringify(tree);
  }, [body]);

  return <div className="mp-git-detail__body mp-markdown">{element}</div>;
}
